import React, { useState, useEffect, useCallback } from 'react';
import { Select, Button, Space, Spin, Empty, Tooltip, Tabs } from 'antd';
import {
    ReloadOutlined, BarChartOutlined,
    GlobalOutlined, BankOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { localeIntl } from '../../i18n/format';
import TableExportButton from '../../components/TableExportButton';
import PageHeader from '../../components/PageHeader';
import logoLocal from '../../assets/images/logo.png';

// ── Constantes ─────────────────────────────────────────────────────
const ANNEES = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
// Mois en capitales, dans la langue de l'interface (lus au rendu).
const libellesMois = (t) => Array.from({ length: 12 }, (_, i) => t(`commun.moisMajuscules.${i + 1}`));
// Nbre / Montant : codes de ligne, traduits à l'affichage seulement.
const libelleTypeLigne = (type) => i18n.t(`statistiques:cadences.types.${type}`);

// ── Helpers formatage ──────────────────────────────────────────────
const fNb = (v) => (!v || v === 0) ? '-' : Number(v).toLocaleString(localeIntl());
const fMt = (v) => (!v || v === 0) ? '-' : Number(v).toLocaleString(localeIntl(), { minimumFractionDigits: 0 });
const fPct = (nb, dec) => {
    if (!nb || !dec || dec === 0) return '-';
    const p = (nb / dec) * 100;
    return `${p.toFixed(1)} %`;
};

// ── Label d'une année de survenance/paiement (-1 = "anneeMin-1 et ant.") ──
// Convention alignée sur CadenceDto (backend) et reporting/TabCadence.js.
const labelAnnee = (v, anneeMin) => {
    if (v === -1) {
        return anneeMin != null
            ? i18n.t('statistiques:commun.etAnterieures', { annee: anneeMin - 1 })
            : i18n.t('statistiques:commun.anterieures');
    }
    return String(v);
};

// ── Calcul de la ligne TOTAL d'un bloc (le backend ne fournit pas de
//    ligne agrégée par bloc · CadenceDto.BlocCadence n'expose que `lignes`) ──
function computeTotalLigne(bloc) {
    if (!bloc?.lignes?.length) return null;
    const cellules = {};
    let nbTotal = 0;
    let montantTotal = 0;
    let sinistresDeClares = 0;
    bloc.lignes.forEach((ligne) => {
        nbTotal += ligne.nbTotal || 0;
        montantTotal += Number(ligne.montantTotal || 0);
        sinistresDeClares += ligne.sinistresDeClares || 0;
        Object.entries(ligne.cellules || {}).forEach(([anneeKey, cell]) => {
            if (!cellules[anneeKey]) cellules[anneeKey] = { nb: 0, montant: 0 };
            cellules[anneeKey].nb += cell?.nb || 0;
            cellules[anneeKey].montant += Number(cell?.montant || 0);
        });
    });
    return { cellules, nbTotal, montantTotal, sinistresDeClares };
}

// ── Construction colonnes dynamiques ──────────────────────────────
function buildCols(anneesColonnes, anneeMin, t) {
    const cols = [
        {
            title: t('cadences.colonnes.exerciceSurvenance'),
            dataIndex: 'exerciceSurvenance',
            key: 'surv',
            width: 170,
            fixed: 'left',
            render: (v, r) => (
                <span style={{
                    fontWeight: r._isTotal ? 800 : 600,
                    color: r._isTotal ? '#1a6b3c' : '#111827',
                    fontSize: 12,
                }}>{v}</span>
            ),
        },
        {
            title: t('cadences.colonnes.type'),
            dataIndex: '_type',
            key: 'type',
            width: 80,
            fixed: 'left',
            render: (v) => (
                <span style={{
                    fontSize: 10,
                    fontWeight: 600,
                    color: v === 'Nbre' ? '#1d4ed8' : '#7c3aed',
                    background: v === 'Nbre' ? '#eff6ff' : '#f5f3ff',
                    padding: '1px 6px',
                    borderRadius: 4,
                }}>{v}</span>
            ),
        },
    ];

    // Colonnes dynamiques par année de paiement (CadenceDto.anneesColonnes)
    (anneesColonnes || []).forEach((ep) => {
        cols.push({
            title: labelAnnee(ep, anneeMin),
            key: `ep_${ep}`,
            align: 'right',
            width: 100,
            render: (_, r) => {
                // ligne.cellules est une Map<Integer, CelluleCadence> côté backend
                // → objet JS indexé par année, pas un tableau.
                const cell = r._cellules?.[ep];
                const val = r._type === 'Nbre' ? cell?.nb : cell?.montant;
                return (
                    <span style={{
                        fontWeight: r._isTotal ? 700 : 400,
                        color: (val && val !== 0) ? '#111827' : '#d1d5db',
                        fontSize: 12,
                    }}>
                        {r._type === 'Nbre' ? fNb(val) : fMt(val)}
                    </span>
                );
            },
        });
    });

    // Colonne TOTAL
    cols.push({
        title: 'TOTAL',
        key: 'total',
        align: 'right',
        width: 110,
        render: (_, r) => {
            const val = r._type === 'Nbre' ? r.totalNb : r.totalMontant;
            return (
                <span style={{
                    fontWeight: 700,
                    color: r._isTotal ? '#1a6b3c' : '#111827',
                    fontSize: 12,
                }}>
                    {r._type === 'Nbre' ? fNb(val) : fMt(val)}
                </span>
            );
        },
    });

    // Sinistres déclarés (Nbre uniquement)
    cols.push({
        title: t('cadences.colonnes.sinDeclares'),
        key: 'declares',
        align: 'right',
        width: 110,
        render: (_, r) => {
            if (r._type !== 'Nbre') return null;
            return (
                <span style={{ fontWeight: 600, color: '#6b7280', fontSize: 12 }}>
                    {fNb(r.nbDeclares)}
                </span>
            );
        },
    });

    // Taux payé (Nbre uniquement)
    cols.push({
        title: t('cadences.colonnes.tauxPaye'),
        key: 'tauxPaye',
        align: 'right',
        width: 100,
        render: (_, r) => {
            if (r._type !== 'Nbre') return null;
            const pct = fPct(r.totalNb, r.nbDeclares);
            const val = r.totalNb && r.nbDeclares ? (r.totalNb / r.nbDeclares) * 100 : null;
            return (
                <span style={{
                    fontWeight: 700,
                    color: val == null ? '#9ca3af' : val >= 80 ? '#166534' : val >= 50 ? '#92400e' : '#991b1b',
                    fontSize: 12,
                }}>
                    {pct}
                </span>
            );
        },
    });

    return cols;
}

// ── Transformation d'un BlocCadence en lignes de tableau ───────────
function blocToRows(bloc, anneeMin) {
    if (!bloc) return [];
    const allLignes = [...(bloc.lignes || [])];
    const totalLigne = computeTotalLigne(bloc);
    if (totalLigne) allLignes.push({ ...totalLigne, _isTotal: true });

    const rows = [];
    allLignes.forEach((ligne, li) => {
        const label = ligne._isTotal ? 'TOTAL' : labelAnnee(ligne.anneeSurvenance, anneeMin);
        // Ligne Nbre
        rows.push({
            key: `${li}_nb`,
            exerciceSurvenance: label,
            _type: 'Nbre',
            _isTotal: ligne._isTotal || false,
            _cellules: ligne.cellules || {},
            totalNb: ligne.nbTotal,
            totalMontant: ligne.montantTotal,
            nbDeclares: ligne.sinistresDeClares,
        });
        // Ligne Montant
        rows.push({
            key: `${li}_mt`,
            exerciceSurvenance: '',
            _type: 'Montant',
            _isTotal: ligne._isTotal || false,
            _cellules: ligne.cellules || {},
            totalNb: ligne.nbTotal,
            totalMontant: ligne.montantTotal,
            nbDeclares: ligne.sinistresDeClares,
        });
    });
    return rows;
}

// ── Tableau d'un bloc ─────────────────────────────────────────────
function TableauBloc({ bloc, anneesColonnes, anneeMin, loading }) {
    const { t } = useTranslation('statistiques');
    const cols = buildCols(anneesColonnes, anneeMin, t);
    const rows = blocToRows(bloc, anneeMin);

    if (!loading && rows.length === 0) {
        return <Empty description={t('cadences.aucuneDonneeBloc')} style={{ padding: 32 }} />;
    }

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                    <tr>
                        {cols.map(c => (
                            <th key={c.key} style={{
                                background: '#1a6b3c',
                                color: '#fff',
                                padding: '8px 10px',
                                textAlign: c.align === 'right' ? 'right' : 'left',
                                fontWeight: 700,
                                fontSize: 11,
                                whiteSpace: 'nowrap',
                                borderRight: '1px solid #155d33',
                                minWidth: c.width || 80,
                            }}>
                                {c.title}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, ri) => {
                        const isSurv = row._type === 'Nbre';
                        const isTotal = row._isTotal;
                        const bg = isTotal
                            ? (isSurv ? '#dcfce7' : '#f0faf4')
                            : (ri % 4 < 2 ? '#fff' : '#f9fafb');
                        return (
                            <tr key={row.key} style={{ background: bg }}>
                                {cols.map(c => {
                                    let content;
                                    if (c.key === 'surv') {
                                        content = (
                                            <span style={{
                                                fontWeight: isTotal ? 800 : isSurv ? 600 : 400,
                                                color: isTotal ? '#1a6b3c' : '#111827',
                                            }}>{row.exerciceSurvenance}</span>
                                        );
                                    } else if (c.key === 'type') {
                                        content = (
                                            <span style={{
                                                fontSize: 10,
                                                fontWeight: 600,
                                                color: row._type === 'Nbre' ? '#1d4ed8' : '#7c3aed',
                                                background: row._type === 'Nbre' ? '#eff6ff' : '#f5f3ff',
                                                padding: '1px 6px',
                                                borderRadius: 4,
                                            }}>{libelleTypeLigne(row._type)}</span>
                                        );
                                    } else if (c.render) {
                                        content = c.render(null, row);
                                    } else {
                                        content = null;
                                    }
                                    return (
                                        <td key={c.key} style={{
                                            padding: '5px 10px',
                                            textAlign: c.align === 'right' ? 'right' : 'left',
                                            borderBottom: '1px solid #f3f4f6',
                                            borderRight: '1px solid #f3f4f6',
                                            fontWeight: isTotal ? 700 : 400,
                                            borderTop: isSurv && isTotal ? '2px solid #1a6b3c' : undefined,
                                        }}>
                                            {content}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ── En-tête d'un bloc ─────────────────────────────────────────────
function BlocHeader({ icon, title, codePays }) {
    return (
        <div style={{
            padding: '12px 20px 10px',
            borderBottom: '1px solid #f3f4f6',
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#f8fffe',
        }}>
            {icon}
            <span style={{ fontWeight: 800, color: '#1a6b3c', fontSize: 14 }}>{title}</span>
            {codePays && (
                <span style={{
                    fontSize: 10, color: '#6b7280', background: '#f3f4f6',
                    padding: '1px 8px', borderRadius: 20, marginLeft: 4, fontWeight: 600,
                }}>
                    {codePays}
                </span>
            )}
        </div>
    );
}

// ── Flattening bloc cadence pour export ───────────────────────────
function flattenBloc(bloc, anneesColonnes, anneeMin) {
    if (!bloc) return [];
    const allLignes = [...(bloc.lignes || [])];
    const totalLigne = computeTotalLigne(bloc);
    if (totalLigne) allLignes.push({ ...totalLigne, _isTotal: true });

    const rows = [];
    allLignes.forEach((ligne) => {
        const label = ligne._isTotal ? 'TOTAL' : labelAnnee(ligne.anneeSurvenance, anneeMin);
        const cellMap = (type) => (anneesColonnes || []).reduce((acc, ep) => {
            const cell = ligne.cellules?.[ep];
            acc[`ep_${ep}`] = type === 'nb' ? (cell?.nb || 0) : Number(cell?.montant || 0);
            return acc;
        }, {});
        // Ligne Nbre
        rows.push({
            exercice: label,
            type: 'Nbre',
            ...cellMap('nb'),
            total: ligne.nbTotal || 0,
            declares: ligne.sinistresDeClares || 0,
            tauxPaye: ligne.sinistresDeClares ? ((ligne.nbTotal / ligne.sinistresDeClares) * 100) : null,
        });
        // Ligne Montant (pas de "déclarés" en montant côté backend)
        rows.push({
            exercice: '',
            type: 'Montant',
            ...cellMap('mt'),
            total: Number(ligne.montantTotal || 0),
            declares: null,
            tauxPaye: null,
        });
    });
    return rows;
}

function colsCadenceExport(anneesColonnes, anneeMin, t) {
    const cols = [
        { title: t('cadences.colonnes.exerciceSurvenance'), dataIndex: 'exercice' },
        { title: t('cadences.colonnes.type'), dataIndex: 'type', export: (v) => libelleTypeLigne(v) },
    ];
    // Colonnes "Payé en <année>" : la valeur brute est tantôt un nombre de
    // sinistres, tantôt un montant selon la ligne (type) · un format statique
    // (currency/numeric) ne peut pas exprimer cette double sémantique, d'où
    // l'export personnalisé ci-dessous.
    (anneesColonnes || []).forEach((ep) => {
        cols.push({
            title: t('cadences.export.payeEn', { annee: labelAnnee(ep, anneeMin) }),
            dataIndex: `ep_${ep}`,
            numeric: true,
            export: (v, r) => {
                if (!v || v === 0) return '';
                return r.type === 'Nbre'
                    ? Number(v).toLocaleString(localeIntl())
                    : Number(v).toLocaleString(localeIntl(), { minimumFractionDigits: 0 }) + ' FCFA';
            },
        });
    });
    cols.push({
        title: t('commun.total'),
        dataIndex: 'total',
        numeric: true,
        export: (v, r) => {
            if (!v || v === 0) return '';
            return r.type === 'Nbre'
                ? Number(v).toLocaleString(localeIntl())
                : Number(v).toLocaleString(localeIntl(), { minimumFractionDigits: 0 }) + ' FCFA';
        },
    });
    cols.push({
        title: t('cadences.colonnes.sinDeclares'),
        dataIndex: 'declares',
        numeric: true,
        export: (v) => (v == null || v === 0) ? '' : Number(v).toLocaleString(localeIntl()),
    });
    cols.push({
        title: t('cadences.colonnes.tauxPaye'),
        dataIndex: 'tauxPaye',
        export: (v) => v == null ? '' : `${v.toFixed(1)} %`,
    });
    return cols;
}

// ════════════════════════════════════════════════════════════════════
//  COMPOSANT PRINCIPAL
// ════════════════════════════════════════════════════════════════════
export default function EtatCadence() {
    const { t } = useTranslation('statistiques');
    const MOIS_LABELS = libellesMois(t);
    const MOIS_OPTIONS = MOIS_LABELS.map((l, i) => ({ value: i + 1, label: l }));
    const [annee, setAnnee] = useState(new Date().getFullYear());
    const [mois, setMois] = useState(new Date().getMonth() + 1);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('total');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.get(`/v1/stats/cadence?annee=${annee}&mois=${mois}`);
            setData(res.data?.data);
        } catch {
            toast.error(t('cadences.erreurChargement'));
        } finally {
            setLoading(false);
        }
    }, [annee, mois, t]);

    useEffect(() => { load(); }, [load]);

    // Année pivot pour le libellé "anneeMin-1 et ant." (cf. CadenceDto : anneeMin = anneeRef - 3)
    const anneeMin = data ? data.anneeRef - 3 : null;

    // ── Calcul bloc actif + données exportables ─────────────────────
    // Champs réels de CadenceDto : total / parPays / parCompagnie,
    // BlocCadence.label / BlocCadence.code (cf. cbconnect_backend
    // module/statistiques/dto/CadenceDto.java).
    const blocActif = (() => {
        if (!data) return { bloc: null, libelle: t('cadences.totalToutesZones') };
        if (activeTab === 'total') return { bloc: data.total, libelle: t('cadences.totalToutesZones') };
        if (activeTab.startsWith('pays_')) {
            const lib = activeTab.substring(5);
            const b = (data.parPays || []).find(b => b.label === lib);
            return { bloc: b, libelle: t('cadences.libellePays', { libelle: lib }) };
        }
        if (activeTab.startsWith('comp_')) {
            const lib = activeTab.substring(5);
            const b = (data.parCompagnie || []).find(b => b.label === lib);
            return { bloc: b, libelle: t('cadences.libelleCompagnie', { libelle: lib }) };
        }
        return { bloc: null, libelle: '' };
    })();
    const exportData = blocActif.bloc
        ? flattenBloc(blocActif.bloc, data?.anneesColonnes || [], anneeMin)
        : [];
    const exportCols = colsCadenceExport(data?.anneesColonnes || [], anneeMin, t);

    // Construction des onglets
    const tabItems = [];

    if (data) {
        // Onglet TOTAL
        tabItems.push({
            key: 'total',
            label: (
                <span><BarChartOutlined style={{ marginRight: 5 }} />TOTAL</span>
            ),
            children: (
                <div className="cb-card" style={{ padding: 0, marginTop: 0 }}>
                    <BlocHeader
                        icon={<BarChartOutlined style={{ color: '#1a6b3c', fontSize: 15 }} />}
                        title={t('cadences.totalToutesZonesMajuscules')}
                    />
                    <TableauBloc
                        bloc={data.total}
                        anneesColonnes={data.anneesColonnes}
                        anneeMin={anneeMin}
                        loading={loading}
                    />
                </div>
            ),
        });

        // Onglets PAR PAYS
        (data.parPays || []).forEach((bloc) => {
            tabItems.push({
                key: `pays_${bloc.label}`,
                label: bloc.label,
                children: (
                    <div className="cb-card" style={{ padding: 0, marginTop: 0 }}>
                        <BlocHeader
                            icon={<GlobalOutlined style={{ color: '#1d4ed8', fontSize: 15 }} />}
                            title={bloc.label}
                            codePays={bloc.code}
                        />
                        <TableauBloc
                            bloc={bloc}
                            anneesColonnes={data.anneesColonnes}
                            anneeMin={anneeMin}
                            loading={loading}
                        />
                    </div>
                ),
            });
        });

        // Onglets PAR COMPAGNIE TOGOLAISE
        (data.parCompagnie || []).forEach((bloc) => {
            tabItems.push({
                key: `comp_${bloc.label}`,
                label: (
                    <span>
                        <BankOutlined style={{ marginRight: 4, color: '#7c3aed' }} />
                        {bloc.label}
                    </span>
                ),
                children: (
                    <div className="cb-card" style={{ padding: 0, marginTop: 0 }}>
                        <BlocHeader
                            icon={<BankOutlined style={{ color: '#7c3aed', fontSize: 15 }} />}
                            title={t('cadences.compagnieTogolaise', { libelle: bloc.label })}
                        />
                        <TableauBloc
                            bloc={bloc}
                            anneesColonnes={data.anneesColonnes}
                            anneeMin={anneeMin}
                            loading={loading}
                        />
                    </div>
                ),
            });
        });
    }

    return (
        <Layout title={t('cadences.titreOnglet')}>
            <div style={{ maxWidth: 1300 }}>

                {/* Pas de `compte` : le tableau est une matrice (deux lignes, Nbre et
                    Montant, par exercice de survenance) declinee par onglet - aucun
                    nombre d'elements n'y correspond. */}
                <PageHeader
                    titre={t('cadences.titre')}
                    icone={<BarChartOutlined />}
                    precision={data?.dateArrete ? t('commun.arreteAu', { date: data.dateArrete }) : undefined}
                    actions={(
                        <Space wrap>
                            <Select
                                value={annee}
                                onChange={setAnnee}
                                style={{ width: 100, borderRadius: 8 }}
                                options={ANNEES.map(a => ({ value: a, label: a }))}
                            />
                            <Select
                                value={mois}
                                onChange={setMois}
                                style={{ width: 140, borderRadius: 8 }}
                                options={MOIS_OPTIONS}
                            />
                            <Tooltip title={t('commun.actualiser')}>
                                <Button icon={<ReloadOutlined />} onClick={load} loading={loading} style={{ borderRadius: 8 }} />
                            </Tooltip>
                            <TableExportButton
                              floating
                                data={exportData}
                                columns={exportCols}
                                filename={`etat-cadences-${MOIS_LABELS[mois - 1].toLowerCase()}-${annee}-${activeTab}`}
                                title={t('cadences.export.titre', { mois: MOIS_LABELS[mois - 1], annee })}
                                subtitle={t('cadences.export.sousTitre', { libelle: blocActif.libelle, mois: MOIS_LABELS[mois - 1], annee })}
                                logo={logoLocal}
                                showTotals={false}
                                disabled={!data || exportData.length === 0}
                            />
                        </Space>
                    )}
                />

                {/* Légende types */}
                <div style={{
                    display: 'flex', gap: 16, marginBottom: 12,
                    padding: '8px 16px', background: '#f8fffe',
                    border: '1px solid #d1fae5', borderRadius: 8,
                    fontSize: 11, color: '#374151',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ background: '#eff6ff', color: '#1d4ed8', padding: '1px 8px', borderRadius: 4, fontWeight: 700 }}>{libelleTypeLigne('Nbre')}</span>
                        <span>{t('cadences.legende.nbre')}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ background: '#f5f3ff', color: '#7c3aed', padding: '1px 8px', borderRadius: 4, fontWeight: 700 }}>{libelleTypeLigne('Montant')}</span>
                        <span>{t('cadences.legende.montant')}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <BankOutlined style={{ color: '#7c3aed' }} />
                        <span>{t('cadences.legende.compagniesTogolaises')}</span>
                    </div>
                </div>

                {/* Contenu */}
                <Spin spinning={loading}>
                    {!loading && !data ? (
                        <div className="cb-card">
                            <Empty description={t('commun.aucuneDonneePeriode')} style={{ padding: 60 }} />
                        </div>
                    ) : data && tabItems.length > 0 ? (
                        <Tabs
                            activeKey={activeTab}
                            onChange={setActiveTab}
                            items={tabItems}
                            type="card"
                            size="small"
                            style={{ marginTop: 0 }}
                            tabBarStyle={{ marginBottom: 0 }}
                        />
                    ) : loading ? (
                        <div className="cb-card" style={{ padding: 60, textAlign: 'center' }}>
                            <Spin size="large" />
                        </div>
                    ) : null}
                </Spin>

                {/* Footer */}
                {data && (
                    <div style={{ marginTop: 12, fontSize: 11, color: '#9ca3af', textAlign: 'right' }}>
                        {t('cadences.sourceGenereLe', { date: new Date().toLocaleDateString(localeIntl()) })}
                    </div>
                )}
            </div>

            <style>{`
                .ant-tabs-card .ant-tabs-tab {
                    font-size: 12px !important;
                    padding: 5px 12px !important;
                }
                .ant-tabs-card .ant-tabs-tab-active {
                    background: #1a6b3c !important;
                    border-color: #1a6b3c !important;
                }
                .ant-tabs-card .ant-tabs-tab-active .ant-tabs-tab-btn {
                    color: #fff !important;
                }
            `}</style>
        </Layout>
    );
}
